import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FormDrawerWrapper from '../../../../../features/components/FormDrawerWrapper'
import EditSectionsList from '../../../../../features/components/Forms/EditForm/EditSectionsList'
import NameSectionPreview from './Sections/NameSection/NameSectionPreview'
import GoogleAssetsSliderPreview from '../../GoogleAssetsSliderPreview'
import HeadlinesSectionForm from './Sections/HeadlinesSection/HeadlinesSectionForm'
import HeadlinesSectionPreview from './Sections/HeadlinesSection/HeadlinesSectionPreview'
import LinkSectionForm from './Sections/LinkSection/LinkSectionForm'
import LinkSectionPreview from './Sections/LinkSection/LinkSectionPreview'
import DescriptionsSectionForm from './Sections/DescriptionsSection/DescriptionsSectionForm'
import DescriptionsSectionPreview from './Sections/DescriptionsSection/DescriptionsSectionPreview'
import LongHeadlinesSectionPreview from './Sections/LongHeadlinesSection/LongHeadlinesSectionPreview'
import LongHeadlinesSectionForm from './Sections/LongHeadlinesSection/LongHeadlinesSectionForm'
import LandscapeImageSectionForm from './Sections/LandscapeImageSection/LandscapeImageSectionForm'
import LandscapeImageSectionPreview from './Sections/LandscapeImageSection/LandscapeImagePreview'
import SquareImageSectionPreview from './Sections/SquareImageSection/SquareImagePreview'
import SquareImageSectionForm from './Sections/SquareImageSection/SquareImageSectionForm'
import PortraitImageSectionForm from './Sections/PortraitImageSection/PortraitImageSectionForm'
import PortraitImageSectionPreview from './Sections/PortraitImageSection/PortraitImageSectionPreview'
import SquareLogoSectionPreview from './Sections/SquareLogoSection/SquareLogoPreview'
import SquareLogoSectionForm from './Sections/SquareLogoSection/SquareLogoSectionForm'
import LandscapeLogoSectionForm from './Sections/LandscapeLogoSection/LandscapeLogoSectionForm'
import LandscapeLogoSectionPreview from './Sections/LandscapeLogoSection/LandscapeLogoPreview'
import VideoUrlsSectionForm from './Sections/VideoUrlsSection/VideoUrlsSectionForm'
import VideoUrlsSectionPreview from './Sections/VideoUrlsSection/VideoUrlsSectionPreview'
import NameSectionForm from './Sections/NameSection/NameSectionForm'

import useManageEditFormData from '../../../../../hooks/formHooks/useManageEditFormData'

import { createJsonFromQueryString } from '../../../../../helpers/url'

import {
  assetGroupIsLoadingSelector,
  assetGroupSelector,
  assetGroupsSelector,
  assetGroupWasLoadedSelector
} from '../../../../../modules/selectors/assets'
import { clearAssetGroup, getAssetGroup } from '../../../../../modules/actions/assets'
import { selectedGoogleAdAccountSelector } from '../../../../../modules/selectors/adAccounts'

import { ASSET_GROUP_GOOGLE_ADS_EDIT } from '../../../../../constants/forms'
import {
  NAME,
  HEADLINES_LIST,
  CLICK_THROUGH_LINK,
  DESCRIPTIONS_LIST,
  LONG_HEADLINES_LIST,
  LANDSCAPE_IMAGE,
  SQUARE_IMAGE,
  PORTRAIT_IMAGE,
  SQUARE_LOGO,
  LANDSCAPE_LOGO,
  VIDEO_ADS_URL
} from '../../fields'
import { getAssetGroupTransformedValues } from './helpers'

// here defined initial open states for GoogleAssetGroupEdit sections
const sectionsInitialOpenStates = {
  [NAME]: false,
  [CLICK_THROUGH_LINK]: false,
  [HEADLINES_LIST]: false,
  [DESCRIPTIONS_LIST]: false,
  [LONG_HEADLINES_LIST]: false,
  [LANDSCAPE_IMAGE]: false,
  [SQUARE_IMAGE]: false,
  [PORTRAIT_IMAGE]: false,
  [SQUARE_LOGO]: false,
  [LANDSCAPE_LOGO]: false,
  [VIDEO_ADS_URL]: false
}

const GoogleAssetGroupEdit = () => {
  const dispatch = useDispatch()

  const assetGroup = useSelector(assetGroupSelector)
  const assetGroupIsLoading = useSelector(assetGroupIsLoadingSelector)
  const assetGroupWasLoaded = useSelector(assetGroupWasLoadedSelector)

  const selectedAdAccount = useSelector(selectedGoogleAdAccountSelector)
  const assetGroups = useSelector(assetGroupsSelector)

  // if ad is internal, it appears in query params when we open edit form
  const { internal: internalQueryParam } = createJsonFromQueryString(window.location.search)

  const isInternalAssetGroup = internalQueryParam === 'true'

  const getAssetGroupDataHandler = useCallback(
    selectedEditItemId => {
      const currentAssetGroup = assetGroups.find(({ id }) => id === selectedEditItemId)
      const currentAssetGroupAdAccountId = currentAssetGroup?.account

      const adAccount = currentAssetGroupAdAccountId || selectedAdAccount.id

      dispatch(
        getAssetGroup({
          account: adAccount,
          id: selectedEditItemId,
          ...(isInternalAssetGroup && { internal: true })
        })
      )
    },
    [dispatch, assetGroups, selectedAdAccount, isInternalAssetGroup]
  )

  const clearAssetGroupHandler = useCallback(() => {
    dispatch(clearAssetGroup())
  }, [dispatch])

  useManageEditFormData({
    formName: ASSET_GROUP_GOOGLE_ADS_EDIT,
    loadedDataId: assetGroup.id,
    getDataHandler: getAssetGroupDataHandler,
    clearDataHandler: clearAssetGroupHandler
  })

  const isInitialDataLoading = assetGroupIsLoading || !assetGroupWasLoaded

  const formProps = useMemo(() => {
    return {
      isInternalAssetGroup
    }
  }, [isInternalAssetGroup])

  const AdGroupEditSections = useMemo(
    () => [
      {
        sectionName: NAME,
        title: 'Name',
        PreviewComponent: <NameSectionPreview data={assetGroup} />,
        FormComponent: <NameSectionForm />
      },
      {
        sectionName: CLICK_THROUGH_LINK,
        title: 'Link',
        PreviewComponent: <LinkSectionPreview data={assetGroup} />,
        FormComponent: <LinkSectionForm />
      },
      {
        sectionName: HEADLINES_LIST,
        title: 'Headlines',
        PreviewComponent: <HeadlinesSectionPreview data={assetGroup} />,
        FormComponent: <HeadlinesSectionForm />
      },
      {
        sectionName: DESCRIPTIONS_LIST,
        title: 'Descriptions',
        PreviewComponent: <DescriptionsSectionPreview data={assetGroup} />,
        FormComponent: <DescriptionsSectionForm />
      },
      {
        sectionName: LONG_HEADLINES_LIST,
        title: 'Long Headlines',
        PreviewComponent: <LongHeadlinesSectionPreview data={assetGroup} />,
        FormComponent: <LongHeadlinesSectionForm />
      },
      {
        sectionName: LANDSCAPE_IMAGE,
        title: 'Landscape Images',
        PreviewComponent: <LandscapeImageSectionPreview data={assetGroup} />,
        FormComponent: <LandscapeImageSectionForm />
      },
      {
        sectionName: SQUARE_IMAGE,
        title: 'Square Images',
        PreviewComponent: <SquareImageSectionPreview data={assetGroup} />,
        FormComponent: <SquareImageSectionForm />
      },
      {
        sectionName: PORTRAIT_IMAGE,
        title: 'Portrait Images',
        PreviewComponent: <PortraitImageSectionPreview data={assetGroup} />,
        FormComponent: <PortraitImageSectionForm />
      },
      {
        sectionName: SQUARE_LOGO,
        title: 'Square Logos',
        PreviewComponent: <SquareLogoSectionPreview data={assetGroup} />,
        FormComponent: <SquareLogoSectionForm />
      },
      {
        sectionName: LANDSCAPE_LOGO,
        title: 'Landscape Logos',
        PreviewComponent: <LandscapeLogoSectionPreview data={assetGroup} />,
        FormComponent: <LandscapeLogoSectionForm />
      },
      {
        sectionName: VIDEO_ADS_URL,
        title: 'Video Urls',
        PreviewComponent: <VideoUrlsSectionPreview data={assetGroup} />,
        FormComponent: <VideoUrlsSectionForm />
      }
    ],
    [assetGroup]
  )

  const transformedAssetGroupValues = useMemo(() => getAssetGroupTransformedValues(assetGroup), [assetGroup])

  return (
    <FormDrawerWrapper
      title="Edit a Google performance max ad"
      formName={ASSET_GROUP_GOOGLE_ADS_EDIT}
      showOpenButton={false}
    >
      <GoogleAssetsSliderPreview values={transformedAssetGroupValues} showSkeleton={!assetGroupWasLoaded} />
      <EditSectionsList
        editSections={AdGroupEditSections}
        isEditDataLoading={isInitialDataLoading}
        editItemData={assetGroup}
        sectionsInitialOpenStates={sectionsInitialOpenStates}
        formProps={formProps}
      />
    </FormDrawerWrapper>
  )
}

export default GoogleAssetGroupEdit
